// src/components/ApiKeysModal.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import {
  collection,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
} from 'firebase/firestore';
import './ApiKeysModal.css';

interface ApiKey {
  id: string;
  name: string;
  createdAt: number;
  lastUsedAt?: number;
  scopes: string[];
}

interface ApiKeysModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
}

const ALL_SCOPES = [
  { id: 'tasks:read', label: 'Read tasks' },
  { id: 'tasks:write', label: 'Write tasks' },
  { id: 'categories:read', label: 'Read categories' },
  { id: 'categories:write', label: 'Write categories' },
];

async function hashKey(key: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(key);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

function generateApiKey(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const hex = Array.from(array).map(b => b.toString(16).padStart(2, '0')).join('');
  return `sk_live_${hex}`;
}

export const ApiKeysModal: React.FC<ApiKeysModalProps> = ({
  isOpen,
  onClose,
  userId,
}) => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyScopes, setNewKeyScopes] = useState<string[]>([
    'tasks:read',
    'tasks:write',
    'categories:read',
    'categories:write',
  ]);
  const [generatedKey, setGeneratedKey] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadApiKeys = useCallback(async () => {
    try {
      const q = query(
        collection(db, `users/${userId}/apiKeys`),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      const keys = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
      })) as ApiKey[];
      setApiKeys(keys);
    } catch (error) {
      console.error('Error loading API keys:', error);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen && userId) {
      loadApiKeys();
    }
  }, [isOpen, userId, loadApiKeys]);

  const handleGenerate = async () => {
    if (!newKeyName.trim() || newKeyScopes.length === 0) return;

    setIsGenerating(true);
    try {
      const key = generateApiKey();
      const keyHash = await hashKey(key);

      await addDoc(collection(db, `users/${userId}/apiKeys`), {
        name: newKeyName.trim(),
        keyHash,
        createdAt: Date.now(),
        scopes: newKeyScopes,
      });

      setGeneratedKey(key);
      setNewKeyName('');
      await loadApiKeys();
    } catch (error) {
      console.error('Error generating API key:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRevoke = async (keyId: string) => {
    if (!window.confirm('Are you sure you want to revoke this API key? This cannot be undone.')) {
      return;
    }

    try {
      await deleteDoc(doc(db, `users/${userId}/apiKeys/${keyId}`));
      await loadApiKeys();
    } catch (error) {
      console.error('Error revoking API key:', error);
    }
  };

  const handleCopy = async () => {
    if (generatedKey) {
      await navigator.clipboard.writeText(generatedKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const toggleScope = (scopeId: string) => {
    setNewKeyScopes(prev =>
      prev.includes(scopeId)
        ? prev.filter(s => s !== scopeId)
        : [...prev, scopeId]
    );
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content api-keys-modal"
        onClick={e => e.stopPropagation()}
      >
        <h2>API Keys</h2>

        {generatedKey && (
          <div className="new-key-display">
            <div className="new-key-warning">
              Copy this key now. You won't be able to see it again!
            </div>
            <div className="new-key-value">{generatedKey}</div>
            <button className="copy-btn" onClick={handleCopy}>
              {copied ? 'Copied!' : 'Copy to clipboard'}
            </button>
          </div>
        )}

        <div className="api-keys-list">
          {apiKeys.length === 0 ? (
            <div className="no-keys-message">
              No API keys yet. Generate one below.
            </div>
          ) : (
            apiKeys.map(key => (
              <div key={key.id} className="api-key-item">
                <div className="api-key-info">
                  <div className="api-key-name">{key.name}</div>
                  <div className="api-key-meta">
                    Created {formatDate(key.createdAt)}
                    {key.lastUsedAt && ` • Last used ${formatDate(key.lastUsedAt)}`}
                  </div>
                </div>
                <div className="api-key-actions">
                  <button
                    className="revoke-btn"
                    onClick={() => handleRevoke(key.id)}
                  >
                    Revoke
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="generate-section">
          <h3>Generate New Key</h3>
          <div className="generate-form">
            <input
              type="text"
              className="generate-input"
              placeholder="Key name (e.g., OpenClaw Production)"
              value={newKeyName}
              onChange={e => setNewKeyName(e.target.value)}
            />
            <div className="scopes-section">
              <span className="scopes-label">Permissions:</span>
              <div className="scopes-grid">
                {ALL_SCOPES.map(scope => (
                  <label key={scope.id} className="scope-checkbox">
                    <input
                      type="checkbox"
                      checked={newKeyScopes.includes(scope.id)}
                      onChange={() => toggleScope(scope.id)}
                    />
                    {scope.label}
                  </label>
                ))}
              </div>
            </div>
            <button
              className="generate-btn"
              onClick={handleGenerate}
              disabled={!newKeyName.trim() || newKeyScopes.length === 0 || isGenerating}
            >
              {isGenerating ? 'Generating...' : 'Generate Key'}
            </button>
          </div>
        </div>

        <div className="modal-buttons">
          <button className="modal-btn done-btn" onClick={() => {
            setGeneratedKey(null);
            onClose();
          }}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};
